import { motion } from "framer-motion";
import { useT } from "@/lib/i18n";
import SignInForm from "@/components/SignInForm";
import tracking from "@/lib/tracking";
import { BEAT_TRANSITION } from "../beats";
import ShrinkingBeat from "../ShrinkingBeat";
import type { AuthScreenDef, ScreenComponentProps } from "../types";

/**
 * Handoff 3 fused with account creation: beat one says who is already here,
 * beat two explains why an account exists at all, and the sign-in options sit
 * on the same screen.
 *
 * Beat one used to recite the user's own personality answers back at them
 * before the ask. It read as filler at the moment they were being asked for
 * something, and the sentence it built ("... this is exactly what makes a
 * great table") argued they were a good fit for us rather than that there was
 * anyone here for them. The community is the reason to sign up; their own
 * answers are not news to them.
 *
 * The beat shrinks and stays rather than being swapped out, which is the
 * mechanic every other beat in the flow uses. `AnimatePresence mode="wait"`
 * used to unmount it: beat one exited completely before beat two entered, so
 * three short animations played back to back where the rest of the flow makes
 * one continuous move.
 *
 * Auth itself is the shared `SignInForm`, so Apple / Google / email and every
 * platform quirk behind them stay in one implementation.
 */

const STATS = ["members", "bookings", "cities"] as const;

export default function AuthScreen({
  def,
  rt,
}: ScreenComponentProps<AuthScreenDef>) {
  const { t } = useT();
  // Beat state lives in the runner, same as the handoffs. Keeping a local
  // copy here meant the dock still believed this screen was on its first
  // beat once the user had tapped through, so the "tap to continue" hint
  // stayed up alongside the sign-in buttons.
  const beat = rt.beatIndex;

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex-1 flex flex-col justify-center min-h-[220px]">
        {/* Disabled once it has played: at the last beat `advanceBeat` leaves
            the screen, and a stray tap here must not skip signup. */}
        <motion.button
          type="button"
          onClick={rt.advanceBeat}
          disabled={beat !== 0}
          animate={{ marginBottom: beat === 0 ? 0 : 18 }}
          transition={BEAT_TRANSITION}
          className="text-left"
        >
          <ShrinkingBeat shrunk={beat !== 0}>
            <h2 className="font-display font-semibold text-[34px] leading-[1.14] tracking-tight text-neutral-900 dark:text-neutral-50">
              {t(`${def.copyKey}.opening`)}
            </h2>
          </ShrinkingBeat>
        </motion.button>

        {beat === 1 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={BEAT_TRANSITION}
            style={{ overflow: "hidden" }}
          >
            <h2 className="font-display font-semibold text-[28px] leading-[1.16] tracking-tight text-neutral-900 dark:text-neutral-50 max-w-[320px]">
              {t(`${def.copyKey}.title`)}
            </h2>
            <p className="text-[15px] leading-relaxed text-neutral-500 dark:text-neutral-400 mt-3.5 max-w-[320px]">
              {t(`${def.copyKey}.body`)}
            </p>
          </motion.div>
        )}
      </div>

      {beat === 1 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={BEAT_TRANSITION}
            style={{ overflow: "hidden" }}
          >
            {/* Proof sits right above the buttons — the last thing read
                before the tap that costs them something. */}
            <div className="flex items-stretch justify-center gap-4 my-6">
              {STATS.map((stat, i) => (
                <div key={stat} className="flex items-stretch gap-4">
                  {i > 0 && <div className="w-px bg-black/10 dark:bg-white/10" />}
                  <div className="text-center">
                    <div className="font-Poppins font-semibold text-[18px] text-neutral-900 dark:text-neutral-50">
                      {t(`${def.copyKey}.stats.${stat}.value`)}
                    </div>
                    <div className="text-[11px] leading-snug text-neutral-500 dark:text-neutral-400">
                      {t(`${def.copyKey}.stats.${stat}.label`)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="pb-6">
              <SignInForm
                initialView="main"
                asSignUp
                allowSwitch
                onAuthClick={(provider) =>
                  tracking.quizStepperAuthClicked(provider, rt.audience)
                }
                onAuthFail={(provider, errorCode) =>
                  tracking.quizStepperAuthFailed(provider, rt.audience, errorCode)
                }
                // No `next()` here. The runner moves the flow on when it sees
                // a live session, which covers this sign-in and the user who
                // arrived already signed in with one rule instead of two.
                // Advancing here as well raced that and skipped the screen
                // after this one.
                onSuccess={(provider) => {
                  tracking.quizStepperAuthSucceeded(provider, rt.audience);
                  rt.onAuthComplete?.();
                }}
              />
            </div>
          </motion.div>
      )}
    </div>
  );
}
